import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./components/Sidebar";
import DashboardPage from "./pages/Dashboard";
import CompetitorList from "./pages/CompetitorList";
import Changes from "./pages/Changes";
import Pricing from "./pages/Pricing";
import { getManifest, getPipelineData } from "./data";

const PAGES = [
  { key: "dashboard", label: "Dashboard", icon: "📊" },
  { key: "competitors", label: "Competitors", icon: "🏢" },
  { key: "changes", label: "Changes & Alerts", icon: "🔀" },
  { key: "pricing", label: "Pricing", icon: "💰" },
];

export default function App() {
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [pipelineData, setPipelineData] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(() => {
    setRefreshing(true);
    Promise.all([getPipelineData(), getManifest()])
      .then(([pData, mData]) => {
        setPipelineData(pData);
        setManifest(mData);
        setLastRefresh(new Date().toLocaleTimeString());
      })
      .finally(() => {
        setRefreshing(false);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  function renderPage() {
    const sharedProps = { onRefresh: loadData, lastRefresh, refreshing };
    switch (currentPage) {
      case "dashboard":
        return <DashboardPage data={pipelineData} manifest={manifest} loading={loading} {...sharedProps} />;
      case "competitors":
        return <CompetitorList {...sharedProps} />;
      case "changes":
        return <Changes changes={pipelineData?.changes_detected || []} {...sharedProps} />;
      case "pricing":
        return <Pricing {...sharedProps} />;
      default:
        return <DashboardPage data={pipelineData} manifest={manifest} loading={loading} {...sharedProps} />;
    }
  }

  const currentLabel = PAGES.find((p) => p.key === currentPage)?.label || "";

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar
        pages={PAGES}
        currentPage={currentPage}
        onNavigate={setCurrentPage}
        manifest={manifest}
        pipelineData={pipelineData}
      />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-gray-200 px-6">
          <div className="h-14 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">{currentLabel}</h2>
            <div className="flex items-center gap-3">
              {lastRefresh && (
                <span className="text-xs text-gray-400">Updated {lastRefresh}</span>
              )}
              {refreshing && (
                <span className="text-xs text-blue-500 animate-pulse">⟳ Refreshing...</span>
              )}
              <button
                onClick={loadData}
                disabled={refreshing}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  refreshing
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                }`}
              >
                <span className={refreshing ? "animate-spin inline-block" : ""}>⟳</span>
                Refresh
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-7xl mx-auto">{renderPage()}</div>
        </main>
      </div>
    </div>
  );
}
